<!-- src/components/PoiDetailDialog.vue ------------------------------------ -->
<template>
  <!-- A bottom sheet on a phone, a right-hand panel from 992px. The map is the
       thing being explored, so on a wide screen the sheet must not lie across
       the point that was just clicked.

       Pinned (the 3D peak page) it is not a modal at all: always open on one
       POI, with no close button, the way the apps embed their POI sheet in the
       flyover. On a phone the handle moves it between a peek and full height. -->
  <div
    class="poi-sheet"
    :class="rootClasses"
    id="poi-detail-dialog"
    :tabindex="pinnedPoi ? null : -1"
    :role="pinnedPoi ? null : 'dialog'"
    :aria-hidden="pinnedPoi ? null : 'true'"
  >
    <div class="modal-dialog poi-sheet__dialog" role="document" :style="panelStyle">
      <div class="modal-content poi-sheet__content">
        <!-- Grab handle. Decorative on desktop, but it is what tells a phone
             reader the sheet is draggable. -->
        <button
          v-if="pinnedPoi"
          type="button"
          class="poi-sheet__handle-hit"
          :aria-expanded="pinnedExpanded"
          :aria-label="pinnedExpanded ? $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_less') : $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_more')"
          @pointerdown="onHandlePointerDown"
          @pointerup="onHandlePointerUp"
          @pointercancel="handleStartY = null"
          @keydown.enter.prevent="pinnedExpanded = !pinnedExpanded"
          @keydown.space.prevent="pinnedExpanded = !pinnedExpanded"
        >
          <span class="poi-sheet__handle" aria-hidden="true"></span>
        </button>
        <div v-else class="poi-sheet__handle" aria-hidden="true"></div>

        <header class="poi-sheet__header">
          <span v-if="typeLabel" class="poi-chip">
            <span class="material-icons">{{ typeIcon }}</span>
            {{ typeLabel }}
          </span>

          <div class="poi-sheet__tools">
            <div v-if="menuItems.length" class="poi-more" data-poi-more>
              <button
                type="button"
                class="poi-icon-btn"
                :aria-label="$t('map_data_poi_dialog.map_data_poi_detail_dialog.more_options')"
                :aria-expanded="menuOpen"
                @click.stop="menuOpen = !menuOpen"
              >
                <span class="material-icons">more_vert</span>
              </button>

              <div v-if="menuOpen" class="poi-more__menu" @click="menuOpen = false">
                <component
                  :is="item.href ? 'a' : 'button'"
                  v-for="item in menuItems"
                  :key="item.label"
                  class="poi-more__item"
                  :type="item.href ? null : 'button'"
                  :href="item.href"
                  :target="item.href ? '_blank' : null"
                  :rel="item.href ? 'noopener noreferrer' : null"
                  @click="item.action && item.action()"
                >
                  <span class="material-icons">{{ item.icon }}</span>
                  <span>{{ item.label }}</span>
                </component>
              </div>
            </div>

            <button
              v-if="!pinnedPoi"
              type="button"
              class="poi-icon-btn"
              data-bs-dismiss="modal"
              :aria-label="$t('shared.close')"
            >
              <span class="material-icons">close</span>
            </button>
          </div>
        </header>

        <div class="poi-sheet__body">
          <h2 class="poi-sheet__title">{{ displayName }}</h2>
          <p class="poi-sheet__source">{{ sourceLabel }}</p>

          <!-- Removed by an admin since this map's tiles were built: the backend
               answers POI_DELETED. The name stays so the reader knows which
               place it was; nothing else about it is worth showing. -->
          <p v-if="poiRemoved" class="poi-sheet__removed" role="status">
            <span class="material-icons" aria-hidden="true">location_off</span>
            {{ removedMessage }}
          </p>

          <template v-else>
            <div v-if="rating" class="poi-sheet__rating">
              <span v-for="n in filledStarCount(serpRating)" :key="`f${n}`" class="material-icons">star</span>
              <span v-for="n in emptyStarCount(serpRating)" :key="`e${n}`" class="material-icons poi-sheet__star--empty">star</span>
              <span class="poi-sheet__rating-label">{{ rating.label }}</span>
            </div>

            <div class="poi-pills">
              <span v-if="elevationLabel" class="poi-pill">{{ elevationLabel }}</span>
              <a v-if="coordsText" class="poi-pill" :href="coordsLink">{{ coordsText }}</a>
            </div>

            <!-- The three universal actions. Everything context-specific lives in
                 the overflow above, so this row is the same on every POI type. -->
            <div class="poi-actions">
              <!-- Planner only: a control point on the POI. On an empty route it starts the route here. -->
              <button v-if="poi && showAddControlPoint" type="button" class="poi-action" @click="addControlPoint">
                <span class="poi-action__circle"><span class="material-icons">{{ isActiveRouteEmpty ? 'flag' : 'add_road' }}</span></span>
                <!-- Two literal keypaths: the client i18n bundle only ships keys it can see. -->
                <i18n-t v-if="isActiveRouteEmpty" keypath="shared.poi_actions.set_as_start_point" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
                <i18n-t v-else keypath="shared.poi_actions.add_to_route" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </button>

              <button v-if="poi" type="button" class="poi-action" @click="addToWaypoints">
                <span class="poi-action__circle"><span class="material-icons">add_location_alt</span></span>
                <i18n-t keypath="shared.poi_actions.add_to_waypoints" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </button>

              <!-- Planner only: ends one of the days here (picked from a list when there are several). -->
              <button
                v-if="poi && dayEndChoices.length"
                type="button"
                class="poi-action"
                :class="{ 'poi-action--open': dayPickerOpen }"
                :aria-expanded="dayEndChoices.length > 1 ? dayPickerOpen : undefined"
                @click="endDayHere"
              >
                <span class="poi-action__circle"><span class="material-icons">sports_score</span></span>
                <i18n-t keypath="shared.poi_actions.end_day_here" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </button>

              <!-- Planner only: turns this POI into a highlight on the hike being planned. -->
              <button v-if="poi && showMakeHighlight" type="button" class="poi-action" @click="makeHikeHighlight">
                <span class="poi-action__circle"><span class="material-icons">star</span></span>
                <i18n-t keypath="shared.poi_actions.add_hike_highlight" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </button>

              <a
                v-if="streetViewHref"
                class="poi-action"
                :href="streetViewHref"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span class="poi-action__circle"><span class="material-icons">streetview</span></span>
                <i18n-t keypath="shared.poi_actions.street_view" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </a>

              <a
                v-if="drivingHref"
                class="poi-action"
                :href="drivingHref"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span class="poi-action__circle"><span class="material-icons">directions_car</span></span>
                <i18n-t keypath="shared.poi_actions.driving_directions" tag="span" class="poi-action__label" scope="global"><template #br><br /></template></i18n-t>
              </a>
            </div>

            <!-- Which day "End day here" ends here. -->
            <div v-if="dayPickerOpen && dayEndChoices.length > 1" class="poi-day-picker">
              <p class="poi-day-picker__title">{{ $t('map_data_poi_dialog.map_data_poi_detail_dialog.end_which_day_here') }}</p>
              <button
                v-for="choice in dayEndChoices"
                :key="choice.dayNumber"
                type="button"
                class="poi-day-picker__day"
                :class="{ 'poi-day-picker__day--suggested': choice.suggested }"
                @click="attachToDay(choice.dayNumber)"
              >
                <span class="poi-day-picker__badge">{{ choice.dayNumber }}</span>
                <span class="poi-day-picker__text">
                  <span class="poi-day-picker__name">{{ choice.name || $t('map_data_poi_dialog.map_data_poi_detail_dialog.day_number', { number: choice.dayNumber }) }}</span>
                  <small v-if="choice.distanceM !== null" class="poi-day-picker__meta">
                    {{ $t('map_data_poi_dialog.map_data_poi_detail_dialog.from_the_route', { distance: formatDistance(choice.distanceM) }) }}
                  </small>
                </span>
                <span v-if="choice.suggested" class="poi-day-picker__suggested">{{ $t('map_data_poi_dialog.map_data_poi_detail_dialog.suggested_day') }}</span>
              </button>
            </div>

            <!-- A summit gets everything a peak has: its bagging state, the
                 lists it sits on, its facts, and the hikes that climb it. The
                 same component the peak maps use, so one summit cannot read two
                 ways. A hotel or a water source renders none of it. -->
            <PeakSections
              v-if="peakDetail"
              :peak="peakDetail"
              :signed-in="viewerSignedIn"
              :sign-in-path="signInPath"
            >
              <template #extra>
                <!-- Everything below is Google's, and stays behind the same Pro+ gate
                     it was behind before: two free views per reader, then blurred. -->
                <div class="serp-wrapper" v-if="serpResult">
                  <div v-if="shouldBlurSerpData" class="serp-overlay">
                    <button class="btn btn-yellow" @click="goToPurchasePage">
                      {{ viewLocationButtonText }}
                    </button>
                  </div>

                  <div class="serp-data-container" :class="{ 'serp-blurred': shouldBlurSerpData }">
                    <div v-if="collagePhotos.length" class="poi-collage" :class="`poi-collage--${collagePhotos.length}`">
                      <img
                        v-for="(photo, index) in collagePhotos"
                        :key="index"
                        :src="photo"
                        :alt="displayName"
                        loading="lazy"
                      />
                    </div>

                    <p v-if="descriptionText" class="poi-sheet__description">
                      {{ descriptionShown }}
                      <button
                        v-if="descriptionTruncatable"
                        type="button"
                        class="poi-sheet__more"
                        @click="descriptionExpanded = !descriptionExpanded"
                      >{{ descriptionExpanded ? $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_less') : $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_more') }}</button>
                    </p>

                    <ul class="poi-contact">
                      <li v-if="serpAddress">
                        <span class="material-icons">location_on</span>
                        <span>{{ serpAddress }}</span>
                      </li>
                      <li v-if="serpPhone">
                        <span class="material-icons">call</span>
                        <a :href="`tel:${serpPhone}`">{{ serpPhone }}</a>
                      </li>
                      <li v-if="email">
                        <span class="material-icons">mail</span>
                        <a :href="`mailto:${email}`">{{ email }}</a>
                      </li>
                      <li v-if="websiteHref">
                        <span class="material-icons">public</span>
                        <a :href="websiteHref" target="_blank" rel="noopener noreferrer">{{ websiteText }}</a>
                      </li>
                    </ul>

                    <section v-if="reviews.length" class="poi-reviews">
                      <article v-for="(review, index) in reviews" :key="index" class="poi-review">
                        <header class="poi-review__head">
                          <strong>{{ review.author }}</strong>
                          <span v-if="review.rating" class="poi-review__stars">
                            <span v-for="n in filledStarCount(review.rating)" :key="`r${index}-${n}`" class="material-icons">star</span>
                          </span>
                        </header>
                        <p v-if="review.when" class="poi-review__when">{{ review.when }}</p>
                        <p class="poi-review__text">{{ review.text }}</p>
                      </article>
                    </section>
                  </div>
                </div>
              </template>
            </PeakSections>

            <!-- Everything below is Google's, and stays behind the same Pro+ gate
                 it was behind before: two free views per reader, then blurred. -->
            <div class="serp-wrapper" v-if="serpResult && !peakDetail">
              <div v-if="shouldBlurSerpData" class="serp-overlay">
                <button class="btn btn-yellow" @click="goToPurchasePage">
                  {{ viewLocationButtonText }}
                </button>
              </div>

              <div class="serp-data-container" :class="{ 'serp-blurred': shouldBlurSerpData }">
                <div v-if="collagePhotos.length" class="poi-collage" :class="`poi-collage--${collagePhotos.length}`">
                  <img
                    v-for="(photo, index) in collagePhotos"
                    :key="index"
                    :src="photo"
                    :alt="displayName"
                    loading="lazy"
                  />
                </div>

                <p v-if="descriptionText" class="poi-sheet__description">
                  {{ descriptionShown }}
                  <button
                    v-if="descriptionTruncatable"
                    type="button"
                    class="poi-sheet__more"
                    @click="descriptionExpanded = !descriptionExpanded"
                  >{{ descriptionExpanded ? $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_less') : $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_more') }}</button>
                </p>

                <ul class="poi-contact">
                  <li v-if="serpAddress">
                    <span class="material-icons">location_on</span>
                    <span>{{ serpAddress }}</span>
                  </li>
                  <li v-if="serpPhone">
                    <span class="material-icons">call</span>
                    <a :href="`tel:${serpPhone}`">{{ serpPhone }}</a>
                  </li>
                  <li v-if="email">
                    <span class="material-icons">mail</span>
                    <a :href="`mailto:${email}`">{{ email }}</a>
                  </li>
                  <li v-if="websiteHref">
                    <span class="material-icons">public</span>
                    <a :href="websiteHref" target="_blank" rel="noopener noreferrer">{{ websiteText }}</a>
                  </li>
                </ul>

                <section v-if="reviews.length" class="poi-reviews">
                  <article v-for="(review, index) in reviews" :key="index" class="poi-review">
                    <header class="poi-review__head">
                      <strong>{{ review.author }}</strong>
                      <span v-if="review.rating" class="poi-review__stars">
                        <span v-for="n in filledStarCount(review.rating)" :key="`r${index}-${n}`" class="material-icons">star</span>
                      </span>
                    </header>
                    <p v-if="review.when" class="poi-review__when">{{ review.when }}</p>
                    <p class="poi-review__text">{{ review.text }}</p>
                  </article>
                </section>
              </div>
            </div>


            <!-- No Google place behind this POI, which is the common case: show
                 whatever our own record carries. -->
            <template v-else>
              <p v-if="descriptionText" class="poi-sheet__description">
                {{ descriptionShown }}
                <button
                  v-if="descriptionTruncatable"
                  type="button"
                  class="poi-sheet__more"
                  @click="descriptionExpanded = !descriptionExpanded"
                >{{ descriptionExpanded ? $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_less') : $t('map_data_poi_dialog.map_data_poi_detail_dialog.show_more') }}</button>
              </p>

              <ul class="poi-contact">
                <li v-if="phoneNumber">
                  <span class="material-icons">call</span>
                  <a :href="`tel:${phoneNumber}`">{{ phoneNumber }}</a>
                </li>
                <li v-if="email">
                  <span class="material-icons">mail</span>
                  <a :href="`mailto:${email}`">{{ email }}</a>
                </li>
                <li v-if="websiteHref">
                  <span class="material-icons">public</span>
                  <a :href="websiteHref" target="_blank" rel="noopener noreferrer">{{ websiteText }}</a>
                </li>
              </ul>
            </template>
          </template>
        </div>
      </div>
    </div>

    <!-- The 3D page has its own loading screen, and a spinner over the
         flyover would sit on the summit for every SerpApi lookup. -->
    <Loader v-if="showLoader && !pinnedPoi" />

    <!-- Toast for free views notifications -->
    <div class="position-fixed top-0 end-0 p-3" style="z-index: 9999">
      <div
        id="serpFreeViewToast"
        class="toast"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="toast-header bg-yellow">
          <strong class="me-auto">{{ $t('map_data_poi_dialog.map_data_poi_detail_dialog.free_location_view') }}</strong>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="toast"
            :aria-label="$t('shared.close')"
          ></button>
        </div>
        <div class="toast-body">
          {{ toastMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sheetRightEdge } from './helpers/sheetPlacement';
import { nextPinnedExpanded, pinnedElevation } from './helpers/pinnedSheet';
import { defineComponent } from 'vue';
import { Modal, Toast } from 'bootstrap';
import graph_client from '@/utils/graphql/graph_client';
import { GetPOIByID } from '@/utils/graphql/poi-graph';
import { isPoiDeletedError, poiDeletedReason, poiRemovedMessage } from './helpers/poiDeleted';
import { serpClient } from '@/utils/services/SerpApiClient';
import Loader from '@/utils/shared_components/Loader.vue';
import PeakSections from '@/apps/peak_bagging/PeakSections.vue';
import { PEAK_DETAIL_QUERY } from '@/apps/peak_bagging/queries';
import { postGraphql } from '@/apps/peak_bagging/api';
import { OnBoardingGoal } from '@/utils/models/OnBoardingGoal';
import { POIUtility } from '@/utils/models/POI';
import { poiDetailsToWaypointDescriptionHelper } from '@/utils/helpers/PoiDetailsToWaypointDescriptionHelper';
import { poiToHikeHighlight } from '@/utils/helpers/PoiToHikeHighlightHelper';
import { poiSharePayload, poiShareText, poiShareUrl } from '@/utils/helpers/poiSharing';
import { dayEndChoices, onlyDayToEnd } from '@/apps/plan_trail/services/day-end-choices';
import { MeasurementPrefUtility } from '@/utils/helpers/MeasurementPref';
import { emptyStarCount, filledStarCount } from './helpers/starRating';
import { poiStageDestination } from './helpers/poiStageDestination';
import {UPDATE_ACCOMMODATION_MUTATION, UPDATE_AMENITY_MUTATION} from "../../utils/graphql/poi-graph";
import { RevenueCatWrapper } from '@/utils/RevenueCatWrapper';
import { streetViewUrl, drivingDirectionsUrl, formatCoords } from '@/utils/helpers/poiLinks';
import {
  photoCollage,
  normalisePoiReviews,
  plainText,
  needsShowMore,
  truncateAtWord,
  websiteUrl,
  websiteLabel,
  formatElevation,
  ratingSummary,
  googlePlaceLookup,
} from '@/utils/helpers/poiSheet';

export default defineComponent({
  components: { Loader, PeakSections },

  props: {
    // `{ type, id }` of the one POI to show, embedded and always open — the 3D
    // peak page. Null everywhere else, where the sheet is the map's modal and
    // opens through window.showPoi.
    pinnedPoi: { type: Object, default: null },
    // A height for the pinned POI, which GetPOIByID does not carry: the peak's
    // own, or the DEM's once the 3D terrain has loaded.
    fallbackElevation: { type: Number, default: null },
  },

  emits: ['expanded-change'],

  data() {
    return {
      poi: null,
      // The backend says an admin removed this POI (POI_DELETED), and why.
      poiRemoved: false,
      poiRemovedReason: null,
      poiId: null,
      poiType: null,
      serpData: null,
      showLoader: false,
      isProPlus: false,
      viewedPoiIds: [],
      toastMessage: '',
      freeViewsRemaining: 2,
      menuOpen: false,
      dayPickerOpen: false,
      descriptionExpanded: false,
      panelStyle: null,
      justOpened: false,
      // The peak behind this POI, when it is one. GetPOIByID carries none of
      // the peak-specific fields, so a summit is looked up separately.
      peakDetail: null,
      // The peak sections show bagging progress, which only means anything to a
      // signed-in reader.
      //
      // `window.user` is the backend user, written into both layouts by
      // layouts/_user_json_window before the entrypoint runs — so it is there on
      // every page this sheet opens on. An earlier version sniffed for a
      // `data-signed-in` attribute, which the map pages do not carry, so a
      // signed-in reader was told to sign in.
      viewerSignedIn: !!window.user,
      signInPath: '/login',
      // Pinned only: whether the phone sheet is at full height or its peek,
      // and where a drag on its handle began.
      pinnedExpanded: false,
      handleStartY: null,
    };
  },

  watch: {
    pinnedExpanded(expanded) {
      this.$emit('expanded-change', expanded);
    },
  },

  async mounted() {
    // Before the entitlement check, so the POI is being fetched while it runs.
    if (this.pinnedPoi) this.mountPinned();

    this.isProPlus = await RevenueCatWrapper.isProOrProPlus();
    this.loadViewedPoiIds();
    this.freeViewsRemaining = this.getFreeViewsRemaining();

    if (this.pinnedPoi) return;

    const element = document.getElementById('poi-detail-dialog');
    // A floating panel, not a blocking dialog: no backdrop, so the map stays
    // visible and live underneath while the sheet is open — the same way the
    // peak map's own columns behave. Escape still closes it.
    // No backdrop: this floats over a live map rather than blocking it. Belt
    // and braces — if anything ever constructs this modal without the option,
    // a stray backdrop is cleared when the sheet opens rather than greying the
    // map out.
    const modal = new Modal(element, { backdrop: false });
    element.addEventListener('shown.bs.modal', this.clearStrayBackdrop);

    element.addEventListener('hidden.bs.modal', this.onModalHidden);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.positionToMap);
    window.addEventListener('scroll', this.positionToMap, { passive: true });

    window.showPoi = (poiType, poiId, seed = null) => {
      console.info('showPoi', poiType, poiId);
      this.poiId = poiId;
      this.poiType = poiType;
      // Replace any previously-rendered POI immediately so we never show
      // the previous click's data while the new fetch is in flight.
      this.poi = seed;
      this.poiRemoved = false;
      this.poiRemovedReason = null;
      this.serpData = null;
      this.menuOpen = false;
      this.dayPickerOpen = false;
      this.descriptionExpanded = false;
      this.peakDetail = null;
      this.getPOI();
      Modal.getInstance(element).show();
      this.justOpened = true;
      window.setTimeout(() => { this.justOpened = false; }, 0);
      this.$nextTick(this.positionToMap);
    };
  },

  beforeUnmount() {
    if (this.pinnedPoi) {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.positionToMap);
      return;
    }

    const element = document.getElementById('poi-detail-dialog');
    element.removeEventListener('hidden.bs.modal', this.onModalHidden);
    element.removeEventListener('shown.bs.modal', this.clearStrayBackdrop);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.positionToMap);
    window.removeEventListener('scroll', this.positionToMap);
    Modal.getInstance(element).dispose();
    window.showPoi = null;
  },

  methods: {
    /* ─────────────── Star ratings ─────────────── */
    // Third-party SERP ratings drive `v-for` lengths, so they go through
    // these guards rather than straight into the template — see
    // helpers/starRating.ts.
    filledStarCount,
    emptyStarCount,

    /* ─────────────── Pinned ──────────────────────── */
    // No Modal, no window.showPoi and no click-away: the sheet is part of the
    // page. The overflow menu still closes on an outside click.
    mountPinned() {
      this.poiId = this.pinnedPoi.id;
      this.poiType = this.pinnedPoi.type;
      this.getPOI();
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.positionToMap);
      this.$nextTick(this.positionToMap);
    },

    onHandlePointerDown(event) {
      this.handleStartY = event.clientY;
    },

    onHandlePointerUp(event) {
      // A keyboard "click" arrives with no pointerdown; Enter/Space handle it.
      if (this.handleStartY === null) return;
      this.pinnedExpanded = nextPinnedExpanded(this.pinnedExpanded, event.clientY - this.handleStartY);
      this.handleStartY = null;
    },

    /* ─────────────── Modal lifecycle ─────────────── */
    // What an action does with the sheet once it is done. A pinned sheet is
    // part of the page and stays; the modal closes.
    hideSheet() {
      if (this.pinnedPoi) return;
      Modal.getInstance(document.getElementById('poi-detail-dialog'))?.hide();
    },

    onModalHidden() {
      this.poi = this.poiId = this.poiType = this.serpData = this.peakDetail = null;
      this.menuOpen = false;
      this.descriptionExpanded = false;
    },

    /**
     * Pins the panel to the top-right of the map rather than the window.
     *
     * The maps do not all fill the viewport — some sit in a page with chrome
     * above and below — so positioning against the window let the panel hang
     * past the bottom of the map it belongs to. Mapbox stamps `.mapboxgl-map`
     * on whatever container it was given, which is the one thing every one of
     * these pages has in common.
     *
     * The height is clamped to whatever of the map is actually on screen, so a
     * part-scrolled map gets a shorter panel instead of one running off the
     * bottom. Below the desktop breakpoint it stays a bottom sheet and this
     * does nothing.
     */
    positionToMap() {
      if (window.innerWidth < 992) { this.panelStyle = null; return; }

      const map = document.querySelector('.mapboxgl-map');
      if (!map) { this.panelStyle = null; return; }

      const gap = 10;
      const rect = map.getBoundingClientRect();
      const top = Math.max(rect.top, 0) + gap;
      const bottom = Math.min(rect.bottom, window.innerHeight) - gap;
      // Beside the planner's Actions column rather than under it.
      const dock = document.querySelector('[data-map-dock="right"]');
      const rightEdge = sheetRightEdge(rect.right, dock ? dock.getBoundingClientRect().left : null);

      this.panelStyle = {
        top: `${top}px`,
        right: `${Math.max(window.innerWidth - rightEdge, 0) + gap}px`,
        bottom: 'auto',
        left: 'auto',
        maxHeight: `${Math.max(bottom - top, 220)}px`,
      };
    },

    /* Only ever runs when this sheet is the single open modal, so it cannot
       strip the backdrop from a real dialog stacked underneath. */
    clearStrayBackdrop() {
      if (document.querySelectorAll('.modal.show').length !== 1) return;
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    },

    onDocumentClick(event) {
      if (this.menuOpen && !event.target?.closest('[data-poi-more]')) this.menuOpen = false;
      if (this.pinnedPoi) return;

      // Clicking the map closes the panel. Bootstrap normally does this through
      // the backdrop, which this sheet does not have — it floats over a live
      // map rather than blocking it, so the dismissal has to be wired by hand.
      const element = document.getElementById('poi-detail-dialog');
      if (!element?.classList.contains('show')) return;
      // The click that opened it is still travelling up to the document, and
      // would otherwise close the panel in the same gesture that opened it.
      if (this.justOpened) return;
      if (event.target?.closest('.poi-sheet__dialog')) return;

      Modal.getInstance(element)?.hide();
    },

    /* ─────────────── Waypoints ───────────────────── */
    addToWaypoints() {
      if (!window.user) {
        window.onBoardingVue.launchFor(OnBoardingGoal.Waypoints);
        return;
      }
      const type = POIUtility.fromString(this.type);
      this.$store.dispatch('waypoints/createWaypoint', {
        lngLat: this.lngLat,
        title: this.name,
        type: type.poi,
        description: poiDetailsToWaypointDescriptionHelper(this.poi),
      });
      this.hideSheet();
    },

    async addControlPoint() {
      if (!this.poi || !this.$store.state.planTrail) return;
      // Close the modal first
      this.hideSheet();
      // Exactly what dragging the route's end onto this POI does — see addDestinationToRoute.
      await this.$store.dispatch('planTrail/addDestinationToRoute', { destination: this.stageDestination() });
    },



    /* ─────────────── Stage destination ───────────── */
    stageDestination() {
      return poiStageDestination({
        poiType: this.poiType,
        type: this.type,
        poiId: this.poiId,
        id: this.poi.id,
        name: this.serpName || this.name,
        lngLat: this.lngLat,
      });
    },

    // With one day there's nothing to choose; otherwise the days are listed.
    endDayHere() {
      const onlyDay = onlyDayToEnd(this.dayEndChoices);
      if (onlyDay !== null) {
        this.attachToDay(onlyDay);
        return;
      }
      this.dayPickerOpen = !this.dayPickerOpen;
    },

    formatDistance(metres) {
      return MeasurementPrefUtility.preference().smallUnitFormatter(metres, true);
    },

    async attachToDay(dayNumber) {
      if (!this.poi) return;
      const destination = this.stageDestination();
      this.hideSheet();
      await this.$store.dispatch('planTrail/attachStageDestination', { dayNumber, destination });
    },

    /* ─────────────── Hike highlights ─────────────── */
    makeHikeHighlight() {
      if (!this.poi || this.$store.state.planTrail === undefined) return;

      const { lng, lat } = this.lngLat;
      this.$store.commit('planTrail/addHikeHighlight', poiToHikeHighlight({
        type: this.type,
        name: this.name,
        serpName: this.serpName,
        description: this.description,
        lng,
        lat,
        poiClass: this.poiType,
        poiId: this.poiId,
      }));
      this.$store.dispatch('planTrail/refreshHighlights');

      this.hideSheet();
    },

    /* ─────────────── Data fetch ──────────────────── */
    /**
     * The peak behind this POI, for the shared peak sections.
     *
     * Only for summits: a hotel has no bagging state, no lists and no routes
     * that climb it, so nothing is asked for and nothing is rendered.
     */
    async loadPeakDetail(id) {
      try {
        const data = await postGraphql(PEAK_DETAIL_QUERY, { id });
        if (this.poiId === id) this.peakDetail = data.amenityById || null;
      } catch {
        // The sheet is still useful without them — the peak sections simply do
        // not appear.
      }
    },

    async getPOI() {
      if (!this.poiId || !this.poiType)
        throw new Error('POI id/type missing');

      const requestedId = this.poiId;
      const requestedType = this.poiType;

      try {
        this.showLoader = true;

        const { poi } = await graph_client.request(GetPOIByID, {
          type: requestedType,
          id: requestedId,
        });

        // Bail if the user clicked a different POI while this request was in flight
        if (this.poiId !== requestedId || this.poiType !== requestedType) return;

        this.poi = poi;
        if (poi?.type === 'peak') this.loadPeakDetail(requestedId);

        const lookup = googlePlaceLookup(
          poi.place_id || poi.google_place_id || poi.placeId || null,
          poi.type,
        );
        if (!lookup) {
          this.serpData = null;
          return;
        }

        let result;
        if (lookup.kind === 'place') {
          result = await serpClient.place(lookup.placeId);
        } else {
          const position = { lat: poi.lonlat[1], lng: poi.lonlat[0] };
          result = await serpClient.local({
            name: poi.name ?? '',
            otherInfo: poi.type ?? '',
            position,
          });
        }

        if (this.poiId !== requestedId || this.poiType !== requestedType) return;

        this.serpData = { result };
        if (result.place_id && this.poiId) {
          if (poi.klass == "Accommodation") {
            graph_client.request(UPDATE_ACCOMMODATION_MUTATION, {
              input: {
                id: this.poiId,
                attrs: {
                  placeId: result.place_id,
                },
              },
            });
          } else {
            graph_client.request(UPDATE_AMENITY_MUTATION, {
              input: {
                id: this.poiId,
                attrs: {
                  placeId: result.place_id,
                },
              },
            })
          }
        } else if (result.place_id && !this.poiId) {
          console.warn('POI ID missing, skipping placeId update');
        }
      } catch (err) {
        if (this.poiId !== requestedId || this.poiType !== requestedType) return;

        if (isPoiDeletedError(err)) {
          this.poiRemoved = true;
          this.poiRemovedReason = poiDeletedReason(err);
          this.serpData = null;
          return;
        }
        console.warn('SerpApi lookup failed:', err);
        this.serpData = null;
      } finally {
        // Only clear the loader if this is still the active request; otherwise a
        // superseded fetch would hide the loader for a request still in flight.
        if (this.poiId === requestedId && this.poiType === requestedType) {
          this.showLoader = false;
        }
      }
    },

    /* ─────────────── Free Views Management ─────────────── */
    getFreeViewsRemaining() {
      const stored = localStorage.getItem('hiiker_serp_free_views_remaining');
      return stored !== null ? parseInt(stored, 10) : 2;
    },

    setFreeViewsRemaining(count) {
      localStorage.setItem('hiiker_serp_free_views_remaining', count.toString());
    },

    loadViewedPoiIds() {
      const stored = localStorage.getItem('hiiker_serp_viewed_poi_ids');
      this.viewedPoiIds = stored ? JSON.parse(stored) : [];
    },

    saveViewedPoiIds() {
      localStorage.setItem('hiiker_serp_viewed_poi_ids', JSON.stringify(this.viewedPoiIds));
    },

    hasViewedCurrentPoi() {
      return this.viewedPoiIds.includes(this.poiId);
    },

    // Hands the POI's link to the OS share sheet where there is one, and falls
    // back to the clipboard. A cancelled share sheet throws AbortError, which is
    // the walker changing their mind rather than a failure — it must not fall
    // through to a clipboard write they did not ask for.
    async sharePOI() {
      const payload = poiSharePayload(this.poi);
      if (!payload) return;

      if (this.canUseShareSheet) {
        try {
          await navigator.share(payload);
        } catch (e) {
          if (e && e.name !== 'AbortError') this.showToast(this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.could_not_share_place'));
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(poiShareText(this.poi));
        this.showToast(this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.link_copied'));
      } catch (e) {
        this.showToast(this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.could_not_copy_link'));
      }
    },

    showToast(message) {
      this.toastMessage = message;
      const toastElement = document.getElementById('serpFreeViewToast');
      const toast = new Toast(toastElement, {
        autohide: true,
        delay: 5000
      });
      toast.show();
    },

    goToPurchasePage() {
      // If this POI has already been viewed for free, just reveal it
      if (this.hasViewedCurrentPoi()) {
        return; // shouldBlurSerpData will handle showing it
      }

      // If they have free views remaining, use one
      if (this.freeViewsRemaining > 0) {
        const newCount = this.freeViewsRemaining - 1;

        // Update both reactive data and localStorage
        this.freeViewsRemaining = newCount;
        this.setFreeViewsRemaining(newCount);

        // Add this POI to the viewed list
        this.viewedPoiIds.push(this.poiId);
        this.saveViewedPoiIds();

        // Show toast with remaining views
        if (newCount > 0) {
          this.showToast(newCount === 1
            ? this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.one_free_view_remaining')
            : this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.free_views_remaining', { count: newCount }));
        } else {
          this.showToast(this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.last_free_view'));
        }

        // Force re-render by updating the component
        this.$forceUpdate();
      } else {
        // No free views left, redirect to purchases
        window.location.href = '/purchases';
      }
    },

    /* ─────────────── Helpers ─────────────────────── */
    photoSrc(img) {
      // 1. SerpApi rich result → serpapi_thumbnail
      if (img.serpapi_thumbnail) return img.serpapi_thumbnail;
      if (img.thumbnail) return img.thumbnail;

      // 2. Fallback for Google photo-reference objects
      if (img.photo_reference) {
        const key = import.meta.env.VITE_GOOGLE_PLACES_KEY;
        return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=1000&photoreference=${img.photo_reference}&key=${key}`;
      }
      return '';
    },
  },

  computed: {
    /* ─────────────── The new sheet's own reading ───────────────── */

    /* One component serves every POI type, so the chip is derived rather than
       branched on — an unmapped type still gets a sensible pin. */
    typeLabel() {
      if (!this.type) return '';
      try {
        return POIUtility.fromString(this.type)?.prettyName() || '';
      } catch {
        return '';
      }
    },

    typeIcon() {
      const icons = {
        peak: 'terrain', water: 'water_drop', drinking_water: 'water_drop',
        hotel: 'hotel', hostel: 'hotel', motel: 'hotel', bnb: 'hotel',
        campground: 'camping', shelter: 'cabin', cave: 'landscape',
        pub: 'sports_bar', fast_food: 'lunch_dining', coffee: 'local_cafe',
        shop: 'storefront', pharmacy: 'local_pharmacy', medical: 'local_hospital',
        parking: 'local_parking', bus: 'directions_bus', viewpoint: 'photo_camera',
        information: 'info', post_office: 'local_post_office', town: 'location_city',
        history: 'museum', gate: 'fence', junction: 'alt_route', ev_charging: 'ev_station',
      };
      return icons[this.type] || 'place';
    },

    /* The name Google knows it by wins, because that is what the rest of the
       panel is describing — our own row is the fallback. */
    displayName() {
      return this.serpName || this.name || this.typeLabel || this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.point_of_interest');
    },

    sourceLabel() {
      return this.serpResult ? this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.google_place') : this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.hiiker_poi');
    },

    coordsText() {
      if (!this.poi) return '';
      const [lon, lat] = this.poi.lonlat;
      return formatCoords(lat, lon);
    },

    elevationLabel() {
      return formatElevation(pinnedElevation(this.poi?.elevation, this.fallbackElevation));
    },

    // Constant for the modal, so Vue never rewrites the class list Bootstrap
    // is toggling `show` on.
    rootClasses() {
      if (!this.pinnedPoi) return 'modal fade';
      return { 'poi-sheet--pinned': true, 'poi-sheet--expanded': this.pinnedExpanded };
    },

    streetViewHref() {
      if (!this.poi) return null;
      const [lon, lat] = this.poi.lonlat;
      return streetViewUrl(lat, lon);
    },

    drivingHref() {
      if (!this.poi) return null;
      const [lon, lat] = this.poi.lonlat;
      return drivingDirectionsUrl(lat, lon);
    },

    rating() {
      return ratingSummary(this.serpRating, this.serpTotalRatings);
    },

    collagePhotos() {
      return photoCollage(this.serpImages.map((img) => ({
        thumbnail: this.photoSrc(img),
        serpapi_thumbnail: '',
      })));
    },

    reviews() {
      return normalisePoiReviews(this.serpResult);
    },

    descriptionText() {
      return plainText(this.description);
    },

    descriptionTruncatable() {
      return needsShowMore(this.descriptionText);
    },

    descriptionShown() {
      return this.descriptionExpanded ? this.descriptionText : truncateAtWord(this.descriptionText);
    },

    websiteHref() {
      return websiteUrl(this.serpWebsite || this.website);
    },

    websiteText() {
      return websiteLabel(this.serpWebsite || this.website) || this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.website');
    },

    /* Everything context-specific. The circular row stays identical across POI
       types; these are the actions only some surfaces or some types offer. */
    removedMessage() {
      return poiRemovedMessage(this.poiRemovedReason, (key) => this.$t(key));
    },

    menuItems() {
      const items = [];
      if (!this.poi || this.poiRemoved) return items;

      // Pinned, the sheet is already on the peak's 3D page.
      if (this.type === 'peak' && !this.pinnedPoi) {
        // The peak's own address when the backend gave us one. `/peaks/<id>/3d`
        // only redirects to it, and a link in a menu should point at the
        // destination.
        items.push({ label: this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.menu_view_in_3d'), icon: 'terrain', href: this.peakUrl });
      }
      if (this.shareUrl) {
        items.push({ label: this.shareLabel, icon: this.shareIcon, action: this.sharePOI });
      }
      return items;
    },

    /* ─────────────── Sharing ─────────────────────── */
    // Never null for a POI the backend answered for, and rebuilt from the tile's
    // own type and id when it did not — so Share survives a missed fetch, which
    // is the one moment the walker has no other way to get a link.
    shareUrl() { return poiShareUrl(this.poi); },

    // The peak's canonical address, falling back to the id form that 301s to it.
    peakUrl() {
      if (!this.poi) return '#';
      return this.poi.peakUrl || `/peaks/${this.poi.id}`;
    },

    // A share sheet where the browser has one, a clipboard copy otherwise — so
    // the label has to say which is about to happen.
    canUseShareSheet() {
      return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    },
    shareLabel() { return this.canUseShareSheet ? this.$t('shared.share') : this.$t('flyover_share.copy_link'); },
    shareIcon() { return this.canUseShareSheet ? 'share' : 'link'; },

    /* ─────────────── Store checks ───────────────── */
    showAddControlPoint() {
      // Check if planTrail store is available
      return this.$store && this.$store.state.planTrail !== undefined;
    },

    // The active route has no markers yet (or nothing is active) — the same
    // "add control point" action is really "set as start point" in that case,
    // since it's the first marker on an otherwise-empty route.
    isActiveRouteEmpty() {
      const planTrail = this.$store && this.$store.state.planTrail;
      if (!planTrail) return false;
      const route = planTrail.activeRouteKey ? planTrail.routes[planTrail.activeRouteKey] : null;
      return !route || route.markers.length === 0;
    },

    showMakeHighlight() {
      // Only inside the planner, where the hikeHighlights store exists
      return this.$store && this.$store.state.planTrail !== undefined;
    },

    // The days this place could end (planner only), with the selected
    // route's day — or else the nearest — suggested (see dayEndChoices).
    dayEndChoices() {
      const planTrail = this.$store?.state?.planTrail;
      if (!planTrail || !this.poi?.lonlat) return [];
      return dayEndChoices(planTrail.routes || {}, planTrail.dayInfo, planTrail.activeRouteKey, this.poi.lonlat);
    },

    /* ─────────────── DB POI basics ───────────────── */
    type() { return this.poi ? this.poi.type : ''; },
    name() { return this.poi ? this.poi.name : ''; },
    phoneNumber() { return this.poi ? this.poi.international_phone_number : ''; },
    website() {
      if (!this.poi || !this.poi.website) return '';
      return /^https?:\/\/$/.test(this.poi.website) ? '' : this.poi.website;
    },
    email() { return this.poi ? this.poi.email : ''; },
    description() { return this.poi ? this.poi.description : ''; },
    coords() {
      if (!this.poi) return '';
      const [lon, lat] = this.poi.lonlat;
      return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    },
    lngLat() {
      if (!this.poi) throw new Error('POI null');
      const [lng, lat] = this.poi.lonlat;
      return { lng, lat };
    },
    coordsLink() {
      if (!this.poi) return '#';
      const [lon, lat] = this.poi.lonlat;
      return `/map?lng=${lon.toFixed(5)}&lat=${lat.toFixed(5)}`;
    },

    /* ─────────────── SerpApi result ─────────────── */
    serpResult() { return this.serpData?.result || null; },

    /* Check if SERP data should be blurred */
    shouldBlurSerpData() {
      if (this.isProPlus) return false;

      // If this POI has been viewed with a free view, don't blur
      if (this.hasViewedCurrentPoi()) return false;

      // Check if there's contact info (phone, email, or description)
      const hasContactInfo = !!(this.serpPhone || this.email || this.description);

      // Check if there are photos or reviews
      const hasMediaOrReviews = this.serpImages.length > 0 || this.serpReviews.length > 0;

      // Only blur if both conditions are met
      return hasContactInfo && hasMediaOrReviews;
    },

    viewLocationButtonText() {
      if (this.freeViewsRemaining > 0) {
        return this.freeViewsRemaining === 1
          ? this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.view_location_info_one_free')
          : this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.view_location_info_free_count', { count: this.freeViewsRemaining });
      }
      return this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.upgrade_to_view_more');
    },

    serpName() { return this.serpResult?.name || ''; },
    serpRating() { return this.serpResult?.rating || 0; },
    serpTotalRatings() { return this.serpResult?.user_ratings_total || 0; },
    serpPhone() {
      const r = this.serpResult;
      return r?.international_phone_number || r?.formatted_phone_number || r?.phone || '';
    },
    serpWebsite() { return this.serpResult?.website || ''; },
    serpAddress() { return this.serpResult?.formatted_address || this.serpResult?.address || ''; },

    /* Images: rich (`images`) → thin (`photos`) */
    serpImages() {
      if (!this.serpResult) return [];
      if (Array.isArray(this.serpResult.images) && this.serpResult.images.length)
        return this.serpResult.images;
      if (Array.isArray(this.serpResult.photos) && this.serpResult.photos.length)
        return this.serpResult.photos;
      return [];
    },

    /* Reviews: new shape (`user_reviews.most_relevant`) or legacy array */
    serpReviews() {
      const r = this.serpResult;
      if (!r) return [];

      // New rich payload
      if (r.user_reviews?.most_relevant?.length)
        return r.user_reviews.most_relevant.map((rev) => ({
          author: rev.username || rev.author_name || this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.anonymous'),
          rating: rev.rating || 0,
          date: rev.date || rev.relative_time_description || '',
          text: rev.description || rev.text || '',
        }));

      // Previous design (already an array of {author_name, rating, text,…})
      if (Array.isArray(r.reviews))
        return r.reviews.map((rev) => ({
          author: rev.author_name || this.$t('map_data_poi_dialog.map_data_poi_detail_dialog.anonymous'),
          rating: rev.rating || 0,
          date: rev.relative_time_description || '',
          text: rev.text || '',
        }));

      return [];
    },
  },
});
</script>

<style scoped>
/* A POI an admin has removed: one muted line in place of the sheet's body. */
.poi-sheet__removed {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem;
  padding: 0.75rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #a1a4ad;
}

/* "End day here": the days to choose from. */
.poi-day-picker {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.25rem 0 1rem;
  padding: 0.6rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.poi-day-picker__title {
  margin: 0 0 0.25rem 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a1a4ad;
}

.poi-day-picker__day {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.45rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 8px;
  background: none;
  color: #fff;
  text-align: left;
  cursor: pointer;
}

.poi-day-picker__day:hover {
  background: rgba(255, 255, 255, 0.06);
}

.poi-day-picker__day--suggested {
  border-color: rgba(253, 223, 0, 0.45);
}

/* The planner's yellow stop badge (plan_trail/utils/stageBadgeIcon.ts). */
.poi-day-picker__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fddf00;
  border: 2px solid #17181d;
  color: #17181d;
  font-size: 0.7rem;
  font-weight: 700;
}

.poi-day-picker__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.poi-day-picker__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
}

.poi-day-picker__meta {
  color: #a1a4ad;
}

.poi-day-picker__suggested {
  flex-shrink: 0;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: rgba(253, 223, 0, 0.12);
  color: #fddf00;
  font-size: 0.7rem;
  font-weight: 600;
}

/* The Pro+ overlay and toast keep their own styling; everything describing the
   sheet itself now lives in app.scss, shared with the peak detail panel. */
.serp-wrapper {
  position: relative;
}

.serp-blurred {
  filter: blur(10px);
  pointer-events: none;
  user-select: none;
}

.serp-overlay {
  position: absolute;
  top: 4%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: auto;
}

.btn-yellow {
  background-color: #FBCD01;
  color: #000;
  border: none;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
}

.btn-yellow:hover {
  background-color: #e6ba00;
  color: #000;
}

.bg-yellow {
  background-color: #FBCD01 !important;
  color: #000;
}

.toast-body {
  color: #000;
  background-color: #fff;
}

</style>
